import { Guid } from "guid-typescript";
import { decode as base64_decode, encode as base64_encode } from "base-64";

import Base32 from "./Base32";
import Address from "../types/core/Address";
import BrowserFile from "../types/files/BrowserFile";
import { DropDownOption } from "../components/inputform/DropDown";
import Logger from "./Logger";
import { Side, Size } from "../types/system/Layout";
import ShadowStaticRange from "../types/system/ShadowStaticRange";

class TTSErrorData {
    Message: string;

    constructor(message: string) {
        this.Message = message;
    }
}

class TTSError extends Error {
    data: TTSErrorData;
    info: any;
}

interface CropOptions {
    width?: number;
    height?: number;
    radius?: number;
}

class Utilities {
    static newGuid() {
        return Guid.create();
    }
    /**
     * Removes the data URL prefix and returns only the base64 content.
     * Author: Liam Pearce
     */
    static stripDataUrl(dataUrl: string) {
        if (dataUrl.indexOf("data:") === 0)
            return dataUrl.substring(dataUrl.indexOf(";base64,") + 8);

        return dataUrl;
    }
    /**
     * Determines if a string represents a date value.
     * Author: Liam Pearce
     */
    static stringIsDate(value: string): boolean {
        // If the value is a number, it's not a date
        if (!isNaN(Number(value))) return false;

        // Check various locale date formats
        const localeFormatted = value.toDate().toLocale();
        if (localeFormatted === value) return true;

        const shortLocaleFormatted = value.toDate().toLocaleShort();
        if (shortLocaleFormatted === value) return true;

        const longLocaleFormatted = value.toDate().toLocaleLong();
        if (longLocaleFormatted === value) return true;

        const shortDateFormatted = value.toDate().toLocaleShortDate();
        if (shortDateFormatted === value) return true;

        // If none of the formats match, it's not a date
        return false;
    }
    /**
     * Converts a string to a Date object, handling multiple formats.
     * Author: Liam Pearce
     */
    static stringToDate(value: string): Date {
        // If the value matches the ISO format without milliseconds, add them
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(value))
            value = `${value}.000Z`;

        // Try to parse the date
        let temp = new Date(value);

        // If parsing succeeded, return the date
        if (!isNaN(Number(temp))) return temp;

        // Try to parse as DD/MM/YYYY by swapping to MM/DD/YYYY
        temp = new Date(value.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3"));

        if (!isNaN(Number(temp))) return temp;

        // If all parsing fails, return the current date
        return new Date();
    }
    /**
     * Converts a time string to a Date object using today's date.
     * Author: Liam Pearce
     */
    static stringToTime(value: string): Date {
        // Get the current date
        const now = new Date();
        // If the value is just a time, prepend today's date
        if (/^\d{2}:\d{2}:\d{2}$/.test(value))
            value = `${now.toISOString().split("T")[0]}T${value}.000`;

        // Try to parse the date/time
        let temp = new Date(value);

        if (!isNaN(Number(temp))) return temp;

        // If parsing fails, return the current date
        return new Date();
    }
    /**
     * Checks if a value is null or undefined.
     * Author: Liam Pearce
     */
    static isNull(value: unknown) {
        return typeof value === "undefined" || value === null;
    }
    /**
     * Checks if a value is not null or undefined.
     * Author: Liam Pearce
     */
    static notNull(value: unknown) {
        return typeof value !== "undefined" && value !== null;
    }
    /**
     * Checks if a string or array is not null, undefined, or empty.
     * Author: Liam Pearce
     */
    static notNullOrEmpty(value: string | Array<any>) {
        return (
            typeof value !== "undefined" && value !== null && value.length > 0
        );
    }
    /**
     * Checks if a number is not null, undefined, or zero.
     * Author: Liam Pearce
     */
    static notNullOrZero(value: number) {
        return typeof value !== "undefined" && value !== null && value !== 0;
    }
    static sleep(timeout: number = 0) {
        return new Promise(resolve => setTimeout(resolve, timeout));
    }
    /**
     * Adds a CSS class to a string of classes if not already present.
     * @param current The current class string.
     * @param addition The class to add.
     * @returns The updated class string.
     */
    static addCss(current: string, addition: string) {
        const classes = current
            .split(" ")
            .filter(item => this.notNullOrEmpty(item));
        if (!classes.includes(addition)) classes.push(addition);
        return classes.join(" ");
    }
    /**
     * Removes a CSS class from a string of classes if present.
     * @param current The current class string.
     * @param removal The class to remove.
     * @returns The updated class string.
     */
    static remCss(current: string, removal: string) {
        const classes = current
            .split(" ")
            .filter(item => this.notNullOrEmpty(item));
        if (classes.includes(removal)) classes.remove(removal);
        return classes.join(" ");
    }
    /**
     * Toggles a CSS class in a string of classes, optionally forcing add/remove.
     * @param current The current class string.
     * @param value The class to toggle.
     * @param force If true, forces add; if false, forces remove.
     * @returns The updated class string.
     */
    static toggleCss(current: string, value: string, force?: boolean) {
        const classes = current
            .split(" ")
            .filter(item => this.notNullOrEmpty(item));
        if (this.notNull(force)) {
            if (classes.includes(value) && force === false) {
                classes.remove(value);
            } else if (!classes.includes(value) && force === true) {
                classes.push(value);
            }
        } else {
            if (classes.includes(value)) {
                classes.remove(value);
            } else if (!classes.includes(value)) {
                classes.push(value);
            }
        }
        return classes.join(" ");
    }
    static formatAddress(address: Address) {
        return `${[address.line1, address.line2].join(" ")}, ${[
            address.city,
            address.stateName,
            address.postcode,
        ].join(" ")}, ${address.countryName}`;
    }
    static validEmailAddress(address: string) {
        return String(address)
            .toLowerCase()
            .match(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            );
    }
    static authenticatorCode(length: number): string {
        let result = new Uint16Array(10);
        for (let index = 0; index < length; index++) {
            result[index] = Math.floor(Math.random() * 254) & 0xff;
        }
        return Base32.Encode(result);
    }
    /**
     * This is the standard error object which should
     * be thrown within the TTS Core apps.
     * @param {String} message The error message to be displayed to users.
     * @param {any} additionalInfo Any additional info useful in diagnosing the problem.
     */
    static createError(message: string, additionalInfo?: any): TTSError {
        const error = new TTSError(message);

        error.data = new TTSErrorData(message);
        error.info = additionalInfo;

        return error;
    }
    static coalesce(...args: any[]) {
        for (let index = 0; index < args.length; index++) {
            if (typeof args[0] !== "undefined" && args[0] != null)
                return args[0];
        }
        return null;
    }
    static deserializeInto<T>(data: string, target: T): T {
        Object.assign(target, JSON.parse(data));

        return target;
    }
    static base64Encode(value: string) {
        return base64_encode(value);
    }
    static base64Decode(value: string) {
        return base64_decode(value);
    }
    static base64FromByteArray(value: Uint8Array): string {
        return window.btoa(String.fromCharCode.apply(null, value));
    }
    static base64FromArrayBuffer(value: ArrayBuffer): string {
        return window.btoa(
            String.fromCharCode.apply(null, new Uint8Array(value)),
        );
    }
    static base64ToByteArray(value: string): Uint8Array {
        let binaryData = window.atob(value);

        let byteArrays = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
            byteArrays[i] = binaryData.charCodeAt(i);
        }

        return byteArrays;
    }
    static getAuthJWT(destination: string, response?: string) {
        let headers = JSON.stringify({
            typ: "JWT",
            alg: "RS256",
        });
        let payload = JSON.stringify({
            id: this.newGuid(),
            issued: new Date().toISOString(),
            issuer: "Timetabling Solutions Web Suite",
            dest: destination,
            resp: response || document.location.href,
            nameid: "urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress",
        });

        let headerPayload = `${this.base64Encode(headers)}.${this.base64Encode(
            payload,
        )}`;

        let signature = "";
        // TODO: Here is where we would calculate a valid
        // JWT signature, because we are authoring the JWT
        // it's useless to the server and so is ignored for
        // now.
        let signedJwt = `${headerPayload}.${signature}`;

        return signedJwt;
    }
    static parseAuthJWT(response: string) {
        try {
            let parts = response.split(".");

            let headers = JSON.parse(this.base64Decode(parts[0]));

            let payload = JSON.parse(this.base64Decode(parts[1]));

            // TODO: Here is where we would check a valid
            // JWT signature.

            return {
                headers,
                payload,
            };
        } catch (error) {
            Logger.error("Error parsing JWT");
            Logger.error(error);
            return null;
        }
    }
    static getByteArrayImageType(byteArray: Uint8Array): string {
        if (
            byteArray[0] === 137 &&
            byteArray[1] === 80 &&
            byteArray[2] === 78 &&
            byteArray[3] === 71 &&
            byteArray[4] === 13 &&
            byteArray[5] === 10 &&
            byteArray[6] === 26 &&
            byteArray[7] === 10
        ) {
            return "png";
        } else if (byteArray[0] === 60) {
            return "svg";
        } else if (
            byteArray[0] === 255 &&
            byteArray[1] === 216 &&
            byteArray[byteArray.length - 2] === 255 &&
            byteArray[byteArray.length - 1] === 217
        ) {
            return "jpg";
        } else if (
            byteArray[0] === 47 &&
            byteArray[1] === 49 &&
            byteArray[2] === 46
        ) {
            return "gif";
        } else {
            return "";
        }
    }
    static base64ImageToDataUrl(base64Data: string) {
        let type = "";
        switch (
            this.getByteArrayImageType(this.base64ToByteArray(base64Data))
        ) {
            case "png":
                type = "png";
                break;
            case "svg":
                type = "svg+xml";
                break;
            case "jpg":
                type = "jpg";
                break;
            case "gif":
                type = "gif";
                break;
        }
        return `data:image/${type};base64,${base64Data}`;
    }
    /**
     * This method returns a clone of the given object, if the
     * merge value is provided it merges this into the returned object.
     * @param {Object} value The object to clone.
     * @param {Object} merge An object to merge in.
     */
    static clone<T>(value: T, merge?: Object) {
        let result = structuredClone(value);

        if (merge)
            Object.keys(merge).forEach(key => {
                result[key] = merge[key];
            });

        return result;
    }
    /**
     * This method returns the original given object and
     * merges the given merge data into the returned object.
     * @param {Object} value The object to clone.
     * @param {Object} merge An object to merge in.
     */
    static merge<T>(value: T, merge: Object) {
        Object.keys(merge).forEach(key => {
            value[key] = merge[key];
        });

        return value;
    }
    static async waitOn(callback: Function) {
        while (!callback())
            await new Promise(resolve => setTimeout(resolve, 200));
    }
    static async waitFor(timeout: number) {
        await new Promise(resolve => setTimeout(resolve, timeout));
    }
    static async fileTobase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            let reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = function () {
                resolve(reader.result.toString());
            };
            reader.onerror = function (error) {
                reject(Utilities.createError("Error: " + error));
            };
        });
    }
    static getScrollParent(node) {
        if (node == null) {
            return null;
        }

        if (node.scrollHeight > node.clientHeight) {
            return node;
        } else {
            return this.getScrollParent(node.parentNode);
        }
    }
    private static compareValues(
        value1: any,
        value2: any,
        direction: boolean,
    ): number {
        if (direction) return value1 > value2 ? 1 : value1 < value2 ? -1 : 0;
        else return value1 < value2 ? 1 : value1 > value2 ? -1 : 0;
    }
    static getSorter(fields: string[]): (value1: any, value2: any) => any {
        return (a, b) => {
            let result = 0;
            for (const field of fields) {
                const direction = field.indexOf("-") === 0 ? false : true;
                const name = !direction
                    ? field.substring(1, field.length)
                    : field;

                if (
                    typeof a[name] === "string" ||
                    typeof b[name] === "string"
                ) {
                    const aNum = parseFloat(a[name]);
                    const bNum = parseFloat(b[name]);
                    if (!isNaN(aNum) && !isNaN(bNum)) {
                        result = this.compareValues(aNum, bNum, direction);
                    } else {
                        result =
                            (a[name] ?? "").localeCompare(b[name] ?? "") *
                            (direction ? 1 : -1);
                    }
                } else {
                    result = this.compareValues(a[name], b[name], direction);
                }
                if (result !== 0) break;
            }
            return result;
        };
    }
    static getFilterer(template: {}): (value: {}) => boolean;
    static getFilterer(paths: Map<string, any>): (value: {}) => boolean;
    static getFilterer(
        template: {} | Map<string, any>,
    ): (value: {}) => boolean {
        if (template instanceof Map)
            template = Utilities.getPathsToObject(template);

        const fields = Object.keys(template);

        const comparers = new Map<string, (value: any) => boolean>();
        for (const field of fields) {
            const reducer = Utilities.getCompiledPathAccessor(field);
            const tempValue = reducer(template);
            switch (typeof tempValue) {
                case "string":
                    comparers.set(field, value =>
                        String(reducer(value) ?? "")
                            .toLowerCase()
                            .includes(String(tempValue).toLowerCase()),
                    );
                    break;
                case "number":
                    comparers.set(
                        field,
                        value => Number(reducer(value)) === Number(tempValue),
                    );
                    break;
                case "boolean":
                    comparers.set(
                        field,
                        value => Boolean(reducer(value)) === Boolean(tempValue),
                    );
                    break;
                case "object":
                    if (tempValue instanceof Date)
                        comparers.set(field, value =>
                            tempValue.equalsDate(reducer(value)),
                        );
                    else
                        comparers.set(field, value =>
                            Utilities.getFilterer(tempValue)(reducer(value)),
                        );
                    break;
                default:
                    comparers.set(field, value => reducer(value) === tempValue);
                    break;
            }
        }

        return function (value) {
            return fields.every(field => comparers.get(field)(value));
        };
    }
    static getCompiledPathAccessor(path: string): (value: {}) => any {
        // eslint-disable-next-line no-new-func
        return new Function("obj", `return obj.${path}`) as (value: {}) => any;
    }
    static getPathToValue<T>(path: string, value: T): T | undefined {
        const parts = path.split(".");
        let result = parts.reduce((prev, curr) => prev && prev[curr], value);
        return (Utilities.notNull(result) ? result : undefined) as T;
    }
    static setPathToValue(path: string, value: any, obj: any) {
        const parts = path.split(".");
        let current = obj;
        for (let index = 0; index < parts.length; index++) {
            const part = parts[index];
            if (index === parts.length - 1) current[part] = value;
            else {
                current = current[part];
                if (!current) break;
            }
        }
    }
    static getPathsToObject(paths: Map<string, any>) {
        const result = {};

        paths.forEach((value, path) => {
            const parts = path.split(".");
            let current = result;
            for (let index = 0; index < parts.length; index++) {
                const part = parts[index];
                if (index === parts.length - 1) current[part] = value;
                else {
                    if (!current[part]) current[part] = {};
                    current = current[part];
                }
            }
        });

        return result;
    }
    static generatePassword(passwordConfig: number) {
        let config: number = passwordConfig;
        let types: string[] = [];
        let useds: string[] = [];
        let result = "";

        if (!passwordConfig || isNaN(passwordConfig)) config = 180031;

        let length = Math.floor(config / 10000);
        config = config - length * 10000;

        if ((Math.pow(2, 0) & config) > 0)
            types.push("ABCDEFGHIJKLMNOPQRSTUVWXYZ");

        if ((Math.pow(2, 1) & config) > 0)
            types.push("abcdefghijklmnopqrstuvwxyz");

        if ((Math.pow(2, 2) & config) > 0) types.push("0123456789");

        if ((Math.pow(2, 3) & config) > 0)
            types.push("!@#$%^&*?~`<>/\\[]{}|-_=+.,");

        if (types.length === 0) types.push("abcdefghijklmnopqrstuvwxyz");

        while (result.length < length) {
            let temp: string;

            if (useds.length === types.length || result.length < length * 0.5)
                temp = types[Math.floor(Math.random() * types.length)];
            else temp = types.filter(item => !useds.includes(item))[0];

            if (!useds.includes(temp)) useds.push(temp);

            result += temp[Math.floor(Math.random() * temp.length)];
        }

        return result;
    }
    static fileToBuffer(file: File) {
        return new Promise<Array<number>>(resolve => {
            let reader = new FileReader();
            reader.readAsArrayBuffer(file);
            reader.onloadend = (event: ProgressEvent<FileReader>) => {
                if (event.target.readyState === FileReader.DONE)
                    resolve((event.target.result as ArrayBuffer).toArray());
            };
        });
    }
    static fileToBase64(file: File | Blob) {
        return new Promise<string>(resolve => {
            let reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onloadend = (event: ProgressEvent<FileReader>) => {
                if (event.target.readyState === FileReader.DONE) {
                    let value = event.target.result as string;
                    resolve(value.substring(value.indexOf("base64,") + 7));
                }
            };
        });
    }
    static uploadFile(
        accept: string,
        maxCount: number = 1000,
    ): Promise<BrowserFile[]> {
        return new Promise<BrowserFile[]>(resolve => {
            const input = document.createElement("input");
            input.type = "file";
            input.multiple = maxCount !== 1;
            input.accept = accept;

            input.addEventListener("change", async event => {
                resolve(
                    await Utilities.processFileSelections(
                        accept,
                        (event.target as HTMLInputElement).files,
                        maxCount,
                    ),
                );
            });

            input.click();
        });
    }
    static processFileSelections(
        accept: string,
        files: FileList,
        maxCount: number = 1000,
    ): Promise<BrowserFile[]> {
        const acceptedExtensions = accept.split(",").map(item => item.trim());

        return new Promise(async resolve => {
            const processFile = (file: File): Promise<string> => {
                return new Promise(resolve => {
                    const reader = new FileReader();
                    reader.addEventListener("load", event => {
                        let data = event.target.result as string,
                            start = data.indexOf(";base64,") + 8;
                        resolve(data.substring(start, data.length));
                    });
                    reader.readAsDataURL(file);
                });
            };

            const result: BrowserFile[] = [];

            for (
                let index = 0;
                index < files.length && index < maxCount;
                index++
            ) {
                const extension =
                    files[index].name.lastIndexOf(".") > 0
                        ? files[index].name.substring(
                              files[index].name.lastIndexOf("."),
                          )
                        : "";

                if (acceptedExtensions.includes(extension))
                    result.push({
                        name: files[index].name,
                        data: await processFile(files[index]),
                        mime: files[index].type,
                        extn: extension,
                    });
            }

            resolve(result);
        });
    }
    static downloadFile(file: any) {
        const extIndex = file.title.toString().lastIndexOf(".");
        const fileName = file.title.substring(0, extIndex);
        const fileExt = file.title.substring(extIndex);

        const anchor = document.createElement("a");
        anchor.href = `data:${file.mime ?? "application/octet-stream"};base64,${
            file.content
        }`;
        anchor.download = `${fileName
            .replace(/\./g, "-")
            .replace(/:/g, "-")}${fileExt}`;
        anchor.click();
    }
    static createFocusTrap(element: HTMLElement): () => void {
        const focusableEls = [];

        const isVisible = element => {
            const props = window.getComputedStyle(element);
            return props.visibility !== "hidden" && props.display !== "none";
        };

        for (let temp of element?.querySelectorAll(
            'a[href], button, textarea, input, select, [tabindex="0"]',
        ) ?? [])
            if (isVisible(temp)) focusableEls.push(temp);

        const handler = event => {
            if (event.key !== "Tab") return;

            const activeEls = focusableEls.filter(item => !item.disabled);
            const index = activeEls.indexOf(event.target);

            if (index >= 0) {
                if (event.shiftKey) {
                    /* shift + tab */ if (index === 0)
                        activeEls[activeEls.length - 1].focus();
                    else activeEls[index - 1].focus();
                    event.preventDefault();
                } /* tab */ else {
                    if (index === activeEls.length - 1) activeEls[0].focus();
                    else activeEls[index + 1].focus();
                    event.preventDefault();
                }
            } else element?.removeEventListener("keydown", handler);
        };

        element?.addEventListener("keydown", handler);

        const first = focusableEls.find(item => !item.disabled);
        if (first) {
            first.focus();
            try {
                first.select();
            } catch {}
        }

        return () => {
            element?.removeEventListener("keydown", handler);
        };
    }
    static dateAddDays(date: Date, days: number): Date {
        const result = new Date(date.getTime());
        result.setDate(date.getDate() + days);
        return result;
    }
    static dateAddMinutes(date: Date, minutes: number): Date {
        const result = new Date(date.getTime());
        result.setMinutes(date.getMinutes() + minutes);
        return result;
    }
    static dateCompare(date1: Date, date2: Date): number {
        const date1Val = date1.getTime(),
            date2Val = date2.getTime();
        return date1Val > date2Val ? 1 : date1Val < date2Val ? -2 : 0;
    }
    static cleanId(value: string) {
        return value
            .replace(/ /g, "")
            .replace(/\\/g, "")
            .replace(/\//g, "")
            .replace(/-/g, "");
    }

    /**
     * Crops and optionally resizes an image from a data URL, with optional rounded corners.
     * Can be called with either width/height/radius or a CropOptions object.
     * @param imageData The image data as a base64 string or data URL.
     * @param options Either a CropOptions object or the width as a number.
     * @param height The height to crop to (if using numeric params).
     * @param radius The corner radius for rounded corners (if using numeric params or CropOptions).
     * @returns A promise that resolves to the cropped image as a base64 string (without data URL prefix).
     */
    static cropImage(
        imageData: string,
        width: number,
        height: number,
        radius?: number,
    ): Promise<string>;
    static cropImage(imageData: string, options: CropOptions): Promise<string>;
    static cropImage(
        imageData: string,
        options: CropOptions | number,
        height?: number,
        radius: number = 0,
    ) {
        // Determine width, height, and radius from parameters or options object
        let width = typeof options === "number" ? options : options.width;
        height = typeof options === "number" ? height : options.height;
        radius = typeof options === "number" ? radius : options.radius;

        // Return a promise that resolves with the cropped image data
        return new Promise<string>(resolve => {
            // Create an image element and set its source
            const originalImage = new Image();
            originalImage.src = imageData.toDataUrl();

            // Create a canvas and get its 2D context
            const canvas = document.createElement(
                "canvas",
            ) as HTMLCanvasElement;
            const ctx = canvas.getContext("2d");

            // When the image loads, perform cropping and resizing
            originalImage.addEventListener("load", () => {
                let widthRatio = 1;
                let heightRatio = 1;
                // Calculate the scaling ratios for width and height
                if (!!height && originalImage.height > height)
                    heightRatio = height / originalImage.height;
                if (!!width && originalImage.width > width)
                    widthRatio = width / originalImage.width;

                // If no resizing or rounding is needed, return the original image
                if (widthRatio === 1 && heightRatio === 1 && radius === 0) {
                    resolve(imageData);
                    return;
                }
                // Adjust width and height to maintain aspect ratio
                if (widthRatio < heightRatio) {
                    width = originalImage.width * widthRatio;
                    height = originalImage.height * widthRatio;
                } else if (widthRatio > heightRatio) {
                    width = originalImage.width * heightRatio;
                    height = originalImage.height * heightRatio;
                } else {
                    width = originalImage.width;
                    height = originalImage.height;
                }

                // Set the canvas size to the new dimensions
                canvas.width = width;
                canvas.height = height;

                // If a corner radius is specified, create a rounded rectangle clipping path
                if (radius > 0) {
                    ctx.beginPath();
                    ctx.moveTo(radius, 0);
                    ctx.lineTo(width - radius, 0);
                    ctx.quadraticCurveTo(width, 0, width, radius);
                    ctx.lineTo(width, height - radius);
                    ctx.quadraticCurveTo(width, height, width - radius, height);
                    ctx.lineTo(radius, height);
                    ctx.quadraticCurveTo(0, height, 0, height - radius);
                    ctx.lineTo(0, radius);
                    ctx.quadraticCurveTo(0, 0, radius, 0);
                    ctx.closePath();
                    ctx.clip();
                }

                // Draw the image onto the canvas
                ctx.drawImage(originalImage, 0, 0, width, height);
                // Convert the canvas to a data URL and strip the prefix
                resolve(this.stripDataUrl(canvas.toDataURL()));
            });
        });
    }

    /**
     * Resizes an image from a data URL to a maximum size, maintaining aspect ratio.
     * Author: Liam Pearce
     */
    static resizeImage(imageData: string, maxSize: number): Promise<string> {
        // Returns a promise that resolves with the resized image data URL
        return new Promise(resolve => {
            // Create an image element and set its source
            const originalImage = new Image();
            originalImage.src = !imageData.startsWith("data:")
                ? imageData.toDataUrl()
                : imageData;

            const canvas = document.createElement(
                "canvas",
            ) as HTMLCanvasElement;
            const ctx = canvas.getContext("2d");

            // When the image loads, perform resizing
            originalImage.addEventListener("load", () => {
                // If the image is already within the max size, return as is
                if (
                    originalImage.width <= maxSize &&
                    originalImage.height <= maxSize
                )
                    resolve(imageData);

                // Set output size proportional to image aspect ratio
                if (originalImage.height > originalImage.width) {
                    canvas.height = maxSize;
                    canvas.width =
                        maxSize * (originalImage.width / originalImage.height);
                } else {
                    canvas.width = maxSize;
                    canvas.height =
                        maxSize * (originalImage.height / originalImage.width);
                }

                // Resolve with the JPEG data URL of the resized image
                ctx.drawImage(originalImage, 0, 0, canvas.width, canvas.height);
                // Convert the canvas to a data URL and strip the prefix
                resolve(this.stripDataUrl(canvas.toDataURL("image/jpeg", 0.7)));
            });
        });
    }

    static async streamToUint8Array(readable: ReadableStream<Uint8Array>) {
        // Use a reader to get the data chunks
        const reader = readable.getReader();
        const chunks = [];
        let totalLength = 0;

        while (true) {
            // Read a chunk from the stream
            const { done, value } = await reader.read();

            if (done) {
                break; // The stream has ended
            }

            if (value instanceof Uint8Array) {
                chunks.push(value);
                totalLength += value.byteLength;
            } else {
                // Handle potential errors if the stream produces non-Uint8Array chunks
                throw new TypeError("Received non-Uint8Array chunk");
            }
        }

        // Concatenate all chunks into a single Uint8Array
        const result = new Uint8Array(totalLength);
        let offset = 0;
        for (const chunk of chunks) {
            result.set(chunk, offset);
            offset += chunk.byteLength;
        }

        return result;
    }

    static async streamToBlob(readable: ReadableStream<Uint8Array>) {
        return new Blob([await this.streamToUint8Array(readable)]);
    }

    /**
     * Compresses a JavaScript object or value into a base64-encoded, gzipped string.
     * @param data The data to compress (will be JSON-stringified).
     * @returns A promise that resolves to the base64-encoded, gzipped string.
     */
    static async compress(data: any) {
        return await this.compressString(JSON.stringify(data));
    }

    /**
     * Compresses a JavaScript object or value into a base64-encoded, gzipped string.
     * @param data The data to compress (will be JSON-stringified).
     * @returns A promise that resolves to the base64-encoded, gzipped string.
     */
    static async compressString(data: string) {
        const encoder = new TextEncoder();
        const uint8Array = encoder.encode(data);
        const blob = new Blob([uint8Array]);
        return await blob
            .stream()
            .pipeThrough(new CompressionStream("gzip"))
            .toDataUrl();
    }

    /**
     * Decompresses a base64-encoded, gzipped string and returns the decompressed text.
     * @param compressed The base64-encoded, gzipped string to decompress.
     * @returns A promise that resolves to the decompressed text string.
     */
    static async decompress(compressed: string) {
        return await compressed
            .fromBase64()
            .toBlob()
            .stream()
            .pipeThrough(new DecompressionStream("gzip"))
            .toText();
    }

    /**
     * Decompresses and parses a base64-encoded, gzipped JSON string into an object of type T.
     * @param data The base64-encoded, gzipped JSON string to decompress and parse.
     * @returns A promise that resolves to the parsed object of type T.
     */
    static async rehydrate<T>(data: string): Promise<T> {
        const temp = await Utilities.decompress(data);
        try {
            return JSON.parse(temp) as T;
        } catch {
            return temp as T;
        }
    }

    static enumToOptions(
        enumType: Object,
        valueType: string = "string",
    ): DropDownOption[] {
        // Create an array to hold the enum entries
        const result = new Array<DropDownOption>();
        // Get all keys from the enum object
        const keys = Reflect.ownKeys(enumType);
        let order = 1;
        for (const key of keys) {
            const value = enumType[key as any];

            // Only include entries of the specified value type
            if (typeof value === valueType) {
                result.push({
                    // Clean up the key name for display
                    text: key.toString().replace(/_/g, " "),
                    value: value,
                    order: order++,
                } as DropDownOption);
            }
        }
        // Return the array of enum entries
        return result;
    }

    static getSideClass(side?: Side): string {
        switch (side) {
            case "top":
                return "top-";
            case "bottom":
                return "bottom-";
            case "left":
                return "left-";
            case "right":
                return "right-";
            case "vertical":
                return "vertical-";
            case "horizontal":
                return "horizontal-";
            default:
                return "";
        }
    }

    static getSizeClass(size?: Size): string {
        switch (size) {
            case "xtrasmall":
                return "-xsmall";
            case "small":
                return "-small";
            case "large":
                return "-large";
            case "xtralarge":
                return "-xlarge";
            default:
                return "";
        }
    }

    static getSelectionRange(
        target: HTMLElement,
        selection?: Selection,
    ): ShadowStaticRange | undefined {
        if (!selection) return undefined;
        try {
            const real = selection.getRangeAt(0);
            const range: ShadowStaticRange = {
                startContainer: real.startContainer,
                startOffset: real.startOffset,
                endContainer: real.endContainer,
                endOffset: real.endOffset,
                collapsed: real.collapsed,
                type: "Caret",
            };
            if (
                selection.type === "Range" &&
                (target.tagName === "INPUT" || target.tagName === "TEXTAREA")
            ) {
                const temp = target as HTMLInputElement | HTMLTextAreaElement;
                range.startContainer = target;
                range.startOffset = temp.selectionStart;
                range.endContainer = target;
                range.endOffset = temp.selectionEnd;
                range.type = "Range";
            }
            return range;
        } catch {
            return undefined;
        }
    }

    static applySelectionRange(
        target: HTMLElement,
        range: ShadowStaticRange,
    ): Selection | undefined {
        if (!range) return undefined;

        if (
            range.type === "Range" &&
            (target.tagName === "INPUT" || target.tagName === "TEXTAREA")
        ) {
            const temp = target as HTMLInputElement | HTMLTextAreaElement;

            temp.focus();
            temp.setSelectionRange(range.startOffset, range.endOffset);

            return document.getSelection();
        } else {
            const selection = document.getSelection();
            if (!selection) return undefined;

            selection.removeAllRanges();
            const liveRange = document.createRange();
            liveRange.setStart(range.startContainer, range.startOffset);
            liveRange.setEnd(range.endContainer, range.endOffset);
            selection.addRange(liveRange);

            return selection;
        }
    }

    static removeSelection(target: HTMLElement, selection?: Selection) {
        if (!selection) return;

        if (
            selection.type === "Range" &&
            (target.tagName === "INPUT" || target.tagName === "TEXTAREA")
        ) {
            const temp = target as HTMLInputElement | HTMLTextAreaElement;
            temp.value =
                temp.value.slice(0, temp.selectionStart) +
                temp.value.slice(temp.selectionEnd);
        } else {
            const parent = selection.anchorNode.parentElement;
            if (parent && parent.isContentEditable)
                selection.deleteFromDocument();
        }
    }

    private static debouceKeys = new Map<string, string>();
    /**
     * Returns a debounced version of a callback function.
     * Author: Liam Pearce
     */
    static debounce(
        callback: (...params: Array<any>) => unknown,
        wait: number,
        name?: string,
    ) {
        name = name ?? this.newGuid().toString();
        return (...params: Array<any>) => {
            let myKey = this.newGuid().toString();
            this.debouceKeys.set(name, myKey);
            setTimeout(() => {
                if (myKey !== this.debouceKeys.get(name)) return;
                callback(...params);
            }, wait);
        };
    }

    private static throttleKeys = new Map<string, Date>();
    /**
     * Returns a throttled version of a callback function.
     * Author: Liam Pearce
     */
    static throttle(
        callback: (...params: Array<any>) => unknown,
        wait: number,
        name?: string,
    ) {
        name = name ?? this.newGuid().toString();
        return (...params: Array<any>) => {
            const now = new Date();
            const lastCall = this.throttleKeys.get(name);
            if (lastCall && now.getTime() - lastCall.getTime() < wait) return;
            this.throttleKeys.set(name, now);
            callback(...params);
        };
    }

    static isValidHttpUrl(httpUrl: string) {
        const pattern = new RegExp(
            "^(https?:\\/\\/)?" + // protocol
                "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
                "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
                "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
                "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
                "(\\#[-a-z\\d_]*)?$", // fragment locator
            "i",
        );
        return pattern.test(httpUrl);
    }

    static stripHiddenElements(htmlString: string, tagName?: string): string {
        // 1. Parse the HTML string into an in-memory document
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, "text/html");

        // 2. Find elements with inline style 'visibility: hidden'
        // This handles variation in spacing/casing like 'visibility : hidden;'
        const inlineHidden = doc.querySelectorAll(
            '[style*="visibility"][style*="hidden"]',
        );
        inlineHidden.forEach((el: HTMLElement) => {
            if (
                el.style.visibility === "hidden" &&
                (!tagName || el.tagName.toLowerCase() === tagName.toLowerCase())
            ) {
                el.remove();
            }
        });

        // 3. Optional: Target the native HTML5 hidden attribute or custom classes
        // doc.querySelectorAll('[hidden]').forEach(el => el.remove());
        // doc.querySelectorAll('.is-hidden, .hidden').forEach(el => el.remove());

        // 4. Serialize the updated DOM back into a clean HTML string
        return doc.body.innerHTML;
    }
}

export default Utilities;
